/**
 * OfflineBanner Component
 * Displays offline status and sync information to users
 * Provides actions for prefetching data and managing offline state
 */

import React, { useState, useEffect } from 'react';
import { useOffline } from '../context/OfflineContext';
import { WifiOff, Wifi, RefreshCw, Download, CloudOff, Check, X, AlertTriangle } from 'lucide-react';

export function OfflineBanner() {
  const { 
    isOnline, 
    isOffline, 
    pendingActions, 
    syncStatus, 
    showOfflineBanner,
    recentlyReconnected,
    forceSync,
    prefetchData,
    dismissBanner 
  } = useOffline();
  
  const [isPrefetching, setIsPrefetching] = useState(false);
  const [prefetchResult, setPrefetchResult] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  // Auto-dismiss success message
  useEffect(() => {
    if (prefetchResult?.success) {
      const timer = setTimeout(() => setPrefetchResult(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [prefetchResult]);

  const handlePrefetch = async () => {
    setIsPrefetching(true);
    setPrefetchResult(null);
    try {
      const result = await prefetchData();
      setPrefetchResult(result);
    } catch (error) {
      setPrefetchResult({ success: false, message: error.message });
    } finally {
      setIsPrefetching(false);
    }
  };

  const handleSync = async () => {
    await forceSync();
  };

  // Offline banner
  if (isOffline && showOfflineBanner) {
    return (
      <div 
        className="fixed bottom-0 left-0 right-0 z-50 bg-amber-500/95 backdrop-blur-sm text-white px-4 py-3 shadow-lg"
        role="alert"
        aria-live="assertive"
        data-testid="offline-banner"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-600 rounded-full">
              <WifiOff className="w-5 h-5" />
            </div>
            <div>
              <p className="font-medium">You're currently offline</p>
              <p className="text-sm text-amber-100">
                {pendingActions > 0 
                  ? `${pendingActions} action${pendingActions > 1 ? 's' : ''} will sync when you reconnect`
                  : 'Some features may be limited. Cached data is available.'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {pendingActions > 0 && (
              <span className="bg-amber-600 px-3 py-1 rounded-full text-sm font-medium">
                {pendingActions} pending
              </span>
            )}
            <button
              onClick={dismissBanner}
              className="p-2 hover:bg-amber-600 rounded-full transition-colors"
              aria-label="Dismiss offline notification"
              data-testid="dismiss-offline-banner"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Recently reconnected banner with sync status
  if (recentlyReconnected || syncStatus === 'syncing') {
    return (
      <div 
        className="fixed bottom-0 left-0 right-0 z-50 bg-emerald-500/95 backdrop-blur-sm text-white px-4 py-3 shadow-lg"
        role="status"
        aria-live="polite"
        data-testid="reconnected-banner"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-600 rounded-full">
              {syncStatus === 'syncing' ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <Wifi className="w-5 h-5" />
              )}
            </div>
            <div>
              <p className="font-medium">
                {syncStatus === 'syncing' ? 'Syncing your changes...' : 'Back online!'}
              </p>
              <p className="text-sm text-emerald-100">
                {syncStatus === 'syncing' 
                  ? 'Please wait while we sync your offline changes'
                  : 'Your connection has been restored'}
              </p>
            </div>
          </div>
          {pendingActions > 0 && syncStatus !== 'syncing' && (
            <button
              onClick={handleSync}
              className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 px-4 py-2 rounded-lg transition-colors"
              data-testid="sync-now-button"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Sync Now</span>
            </button>
          )}
        </div>
      </div>
    );
  }

  return null;
}

/**
 * Offline Status Indicator
 * Small indicator showing current connection status
 */
export function OfflineStatusIndicator({ className = '' }) {
  const { isOnline, pendingActions, syncStatus } = useOffline();

  if (isOnline && pendingActions === 0 && syncStatus === 'idle') {
    return null; // Don't show when everything is normal
  }

  return (
    <div className={`flex items-center gap-2 ${className}`} data-testid="offline-status-indicator">
      {!isOnline ? (
        <>
          <CloudOff className="w-4 h-4 text-amber-500" />
          <span className="text-sm text-amber-600 dark:text-amber-400">Offline</span>
        </>
      ) : syncStatus === 'syncing' ? (
        <>
          <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />
          <span className="text-sm text-blue-600 dark:text-blue-400">Syncing...</span>
        </>
      ) : pendingActions > 0 ? (
        <>
          <AlertTriangle className="w-4 h-4 text-amber-500" />
          <span className="text-sm text-amber-600 dark:text-amber-400">{pendingActions} pending</span>
        </>
      ) : null}
    </div>
  );
}

/**
 * Prefetch Button Component
 * Allows users to manually prefetch data for offline use
 */
export function PrefetchButton({ variant = 'default', className = '' }) {
  const { isOnline, prefetchData } = useOffline();
  const [isPrefetching, setIsPrefetching] = useState(false);
  const [result, setResult] = useState(null);

  const handlePrefetch = async () => {
    if (!isOnline || isPrefetching) return;
    
    setIsPrefetching(true);
    setResult(null);
    
    try {
      const res = await prefetchData();
      setResult(res);
    } catch (error) {
      setResult({ success: false, message: error.message });
    } finally {
      setIsPrefetching(false);
    }
  };

  useEffect(() => {
    if (result) {
      const timer = setTimeout(() => setResult(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [result]);

  if (variant === 'compact') {
    return (
      <button
        onClick={handlePrefetch}
        disabled={!isOnline || isPrefetching}
        className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${
          isPrefetching 
            ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' 
            : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400'
        } ${className}`}
        title="Download data for offline use"
        data-testid="prefetch-button-compact"
      >
        {isPrefetching ? (
          <RefreshCw className="w-5 h-5 animate-spin" />
        ) : result?.success ? (
          <Check className="w-5 h-5 text-emerald-500" />
        ) : (
          <Download className="w-5 h-5" />
        )}
      </button>
    );
  }

  return (
    <button
      onClick={handlePrefetch}
      disabled={!isOnline || isPrefetching}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
        isPrefetching 
          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' 
          : 'bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-300'
      } ${className}`}
      data-testid="prefetch-button"
    >
      {isPrefetching ? (
        <>
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span>Downloading...</span>
        </>
      ) : result?.success ? (
        <>
          <Check className="w-4 h-4 text-emerald-500" />
          <span>Data saved for offline</span>
        </>
      ) : (
        <>
          <Download className="w-4 h-4" />
          <span>Save for offline</span>
        </>
      )}
    </button>
  );
}

/**
 * Offline Settings Panel
 * Full settings panel for managing offline data
 */
export function OfflineSettingsPanel() {
  const { 
    isOnline, 
    pendingActions, 
    lastSyncTime,
    forceSync,
    prefetchData,
    clearData 
  } = useOffline();
  
  const [storageInfo, setStorageInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [action, setAction] = useState(null);

  useEffect(() => {
    // Get storage estimate
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      navigator.storage.estimate().then(estimate => {
        setStorageInfo({
          used: formatBytes(estimate.usage),
          total: formatBytes(estimate.quota),
          percent: ((estimate.usage / estimate.quota) * 100).toFixed(1)
        });
      });
    }
  }, []);

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleAction = async (actionFn, actionName) => {
    setIsLoading(true);
    setAction(actionName);
    try {
      await actionFn();
    } finally {
      setIsLoading(false);
      setAction(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="offline-settings-panel">
      {/* Connection Status */}
      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
        <div className="flex items-center gap-3">
          {isOnline ? (
            <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-full">
              <Wifi className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
          ) : (
            <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-full">
              <WifiOff className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
          )}
          <div>
            <p className="font-medium text-gray-900 dark:text-white">
              {isOnline ? 'Connected' : 'Offline'}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {lastSyncTime 
                ? `Last synced ${new Date(lastSyncTime).toLocaleTimeString()}`
                : 'Not synced yet'}
            </p>
          </div>
        </div>
        {pendingActions > 0 && (
          <span className="px-3 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full text-sm font-medium">
            {pendingActions} pending
          </span>
        )}
      </div>

      {/* Storage Info */}
      {storageInfo && (
        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Offline Storage
          </p>
          <div className="flex items-center gap-4">
            <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-blue-500 transition-all"
                style={{ width: `${Math.min(storageInfo.percent, 100)}%` }}
              />
            </div>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {storageInfo.used} / {storageInfo.total}
            </span>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <button
          onClick={() => handleAction(prefetchData, 'prefetch')}
          disabled={!isOnline || isLoading}
          className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 text-blue-700 dark:text-blue-400 rounded-lg transition-colors disabled:opacity-50"
          data-testid="prefetch-data-button"
        >
          {isLoading && action === 'prefetch' ? (
            <RefreshCw className="w-5 h-5 animate-spin" />
          ) : (
            <Download className="w-5 h-5" />
          )}
          <span>Download Data</span>
        </button>

        <button
          onClick={() => handleAction(forceSync, 'sync')}
          disabled={!isOnline || pendingActions === 0 || isLoading}
          className="flex items-center justify-center gap-2 px-4 py-3 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:hover:bg-emerald-900/50 text-emerald-700 dark:text-emerald-400 rounded-lg transition-colors disabled:opacity-50"
          data-testid="sync-data-button"
        >
          {isLoading && action === 'sync' ? (
            <RefreshCw className="w-5 h-5 animate-spin" />
          ) : (
            <RefreshCw className="w-5 h-5" />
          )}
          <span>Sync Now</span>
        </button>

        <button
          onClick={() => handleAction(clearData, 'clear')}
          disabled={isLoading}
          className="flex items-center justify-center gap-2 px-4 py-3 bg-red-50 hover:bg-red-100 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-700 dark:text-red-400 rounded-lg transition-colors disabled:opacity-50"
          data-testid="clear-offline-data-button"
        >
          {isLoading && action === 'clear' ? (
            <RefreshCw className="w-5 h-5 animate-spin" />
          ) : (
            <X className="w-5 h-5" />
          )}
          <span>Clear Data</span>
        </button>
      </div>

      {/* Info */}
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Downloaded data allows you to view critical information when you have a spotty connection. 
        Changes made offline will automatically sync when your connection is restored.
      </p>
    </div>
  );
}

export default {
  OfflineBanner,
  OfflineStatusIndicator,
  PrefetchButton,
  OfflineSettingsPanel
};
